import { useRouter } from "@tanstack/react-router";
import type { SafetyPolicyType } from "@unbox/api";
import {
  AlertCircle,
  AlertTriangle,
  BarChartSquareMinus,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  CornerUpLeft,
  Edit05,
  InfoCircle,
  LinkBroken01,
  Plus,
  Star06,
  UserX01,
  VideoRecorderOff,
} from "@untitledui/icons";
import type { FC } from "react";
import { useState } from "react";
import { useMessageTemplates } from "@/api/message-templates";
import { useSequence } from "@/api/sequences";
import { Button } from "@/components/base/buttons/button";
import { Select } from "@/components/base/select/select";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useMerchant } from "@/providers/MerchantProvider";
import { stepsToActionSteps } from "./policy-steps";
import type { PolicyOption } from "./VideoDetail";
import { WhatsNextTimeline } from "./whats-next-timeline";

type PolicyIconMap = Record<SafetyPolicyType, FC>;

const POLICY_ICON_BY_TYPE: PolicyIconMap = {
  end_creator_relationship: UserX01,
  end_linked_collaboration: LinkBroken01,
  request_video_removal: VideoRecorderOff,
  request_metadata_edit: Edit05,
  warn_and_remove_from_gmv_max: AlertCircle,
  warn_creator: AlertTriangle,
  remove_from_gmv_max_only: BarChartSquareMinus,
  no_action_needed: CheckCircle,
};

type WhatHappensNextProps = {
  policyOptions: PolicyOption[];
  policyId: string | undefined;
  defaultPolicyId: string | undefined;
  onPolicyChange: (policyId: string) => void;
  videoId: string;
  creatorId?: string;
  // Called when the reviewer explicitly enters/leaves "Run different policy" —
  // lets the parent know whether the current policyId should be sent on submit,
  // or left to the backend to auto-resolve from confirmed risks.
  onOverrideChange?: (overridden: boolean) => void;
  // True once every risk on the video has been dismissed. TabReview submits with
  // startPolicies: false whenever this holds, regardless of policyId — so the
  // "will run" copy + timeline below would misrepresent what submitting actually does.
  allRisksDismissed?: boolean;
  dismissedFlagIds: string[];
  stepsEditedBeforeDismissal: number[];
  // Message copy rewritten by the reviewer, keyed by sequence step. Owned by the parent so
  // it can ship with the review submission.
  messageOverrides: Record<number, string>;
  // `content: null` reverts the step back to the policy's own copy.
  onMessageOverrideChange: (step: number, content: string | null) => void;
};

export const WhatHappensNext = ({
  policyOptions,
  policyId,
  defaultPolicyId,
  onPolicyChange,
  videoId,
  creatorId,
  onOverrideChange,
  allRisksDismissed,
  dismissedFlagIds,
  stepsEditedBeforeDismissal,
  messageOverrides,
  onMessageOverrideChange,
}: WhatHappensNextProps) => {
  const merchantId = useMerchant();
  const router = useRouter();
  const [whatsNextOpen, setWhatsNextOpen] = useState(true);
  const [isSwappingPolicy, setIsSwappingPolicy] = useState(false);
  const selectedPolicy = policyOptions.find((opt) => opt.id === policyId);
  const { data: sequence } = useSequence(selectedPolicy?.sequenceId ?? "", { enabled: !!selectedPolicy?.sequenceId });
  const { data: templates = [] } = useMessageTemplates();
  const steps = stepsToActionSteps(sequence?.steps ?? [], templates);

  const handleResetDefaultPolicy = () => {
    const resetPolicyId = defaultPolicyId ?? policyOptions[0]?.id;
    if (resetPolicyId) onPolicyChange(resetPolicyId);
    setIsSwappingPolicy(false);
    onOverrideChange?.(false);
  };

  const openPolicyDetail = (id: string) => {
    const href = router.buildLocation({
      to: "/merchant/$merchantId/configure-safety",
      params: { merchantId },
      search: { policy: id },
    }).href;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  return (
    <Collapsible open={whatsNextOpen} onOpenChange={setWhatsNextOpen}>
      <div className="bg-unbox-cream-50 flex flex-col gap-4 rounded-md p-4">
        <CollapsibleTrigger className="flex w-full items-center gap-1.5">
          <Star06 className="w-5 h-5 text-secondary" />
          <span className="text-sm font-medium text-secondary flex-1 text-left">What happens next</span>
          {whatsNextOpen ? (
            <ChevronUp className="w-5 h-5 text-secondary" />
          ) : (
            <ChevronDown className="w-5 h-5 text-secondary" />
          )}
        </CollapsibleTrigger>
        <CollapsibleContent className="flex flex-col gap-4">
          {isSwappingPolicy ? (
            <div className="bg-white rounded-md p-3 flex flex-col gap-3 w-full">
              <Select
                size="sm"
                items={policyOptions.map((opt) => ({ ...opt, icon: POLICY_ICON_BY_TYPE[opt.type] }))}
                selectedKey={policyId}
                onSelectionChange={(k) => onPolicyChange(k as string)}
              >
                {(item) => <Select.Item id={item.id} label={item.label} icon={item.icon} />}
              </Select>
              <WhatsNextTimeline
                steps={steps}
                videoId={videoId}
                creatorId={creatorId}
                dismissedFlagIds={dismissedFlagIds}
                stepsEditedBeforeDismissal={stepsEditedBeforeDismissal}
                messageOverrides={messageOverrides}
                onMessageOverrideChange={onMessageOverrideChange}
                onViewPolicy={() => selectedPolicy && openPolicyDetail(selectedPolicy.id)}
              />
              <Button color="secondary" size="sm" iconLeading={CornerUpLeft} onClick={handleResetDefaultPolicy}>
                Reset to run default policy
              </Button>
            </div>
          ) : (
            <>
              <div className="bg-gradient-to-b from-bg-brand-solid to-[#3e2356] rounded-md p-4 w-full">
                <p className="text-sm font-medium text-white">{selectedPolicy?.label}</p>
              </div>
              {allRisksDismissed ? (
                <p className="text-sm font-medium text-primary">
                  All risks on this video have been dismissed, so no policy will run.
                </p>
              ) : (
                <>
                  <p className="text-sm font-medium text-primary">
                    Approving this risk will run the{" "}
                    <button
                      type="button"
                      onClick={() => selectedPolicy && openPolicyDetail(selectedPolicy.id)}
                      className="underline decoration-dotted"
                    >
                      {selectedPolicy?.label}
                    </button>{" "}
                    policy{" "}
                    <Tooltip title="False Claim is confirmed in this video, so the strictest configured policy applies.">
                      <TooltipTrigger className="inline-flex align-middle cursor-pointer">
                        <InfoCircle className="w-4 h-4 -translate-y-px" />
                      </TooltipTrigger>
                    </Tooltip>
                  </p>
                  <WhatsNextTimeline
                    steps={steps}
                    videoId={videoId}
                    creatorId={creatorId}
                    dismissedFlagIds={dismissedFlagIds}
                    stepsEditedBeforeDismissal={stepsEditedBeforeDismissal}
                    messageOverrides={messageOverrides}
                    onMessageOverrideChange={onMessageOverrideChange}
                    onViewPolicy={() => selectedPolicy && openPolicyDetail(selectedPolicy.id)}
                  />
                </>
              )}
              <Button
                color="secondary"
                size="sm"
                iconLeading={Plus}
                className="w-full bg-transparent"
                onClick={() => {
                  setIsSwappingPolicy(true);
                  onOverrideChange?.(true);
                }}
              >
                Run different policy
              </Button>
            </>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
